/**
 * Cleans ECDICT-style raw definitions for learner-facing display.
 * ECDICT raw text often looks like: "n. 動機, 刺激, 推動 [醫] 促動, 推動, 誘導"
 *  - leading POS tag (n. / v. / adj. / ...)
 *  - bracketed domain tags ([醫], [商], [生])
 *  - long comma-joined synonym chains
 *
 * For learning UI we want a single, clean definition phrase.
 */

const POS_PREFIX_RE = /^\s*(?:n|v|vt|vi|adj|adv|prep|conj|pron|int|aux|det|num|art)\s*\.\s*/i;
const DOMAIN_TAG_RE = /\s*\[[^\]]+\]\s*/g;
const REPEATED_PUNCT_RE = /[,，;；]\s*[,，;；]/g;
const LEADING_TRAILING_PUNCT_RE = /^[,，;；\s]+|[,，;；\s]+$/g;

/**
 * Get the first definition line from raw multi-line ECDICT text.
 * ECDICT often crams multiple POS lines separated by '\n'.
 */
function takeFirstLine(raw: string): string {
    const lines = raw.split(/\n|\r\n/).map(l => l.trim()).filter(Boolean);
    return lines[0] ?? raw.trim();
}

/**
 * Cap synonym list — keep at most N comma-separated phrases.
 */
function capSynonyms(s: string, max = 2): string {
    const parts = s.split(/[,，]/).map(p => p.trim()).filter(Boolean);
    if (parts.length <= max) return s.trim();
    return parts.slice(0, max).join(', ');
}

export function cleanDefinition(raw?: string | null, options?: { maxSynonyms?: number }): string {
    if (!raw) return '';
    const max = options?.maxSynonyms ?? 2;
    let s = takeFirstLine(raw);
    s = s.replace(POS_PREFIX_RE, '');
    s = s.replace(DOMAIN_TAG_RE, ', ');
    s = s.replace(REPEATED_PUNCT_RE, ', ');
    s = s.replace(LEADING_TRAILING_PUNCT_RE, '');
    s = capSynonyms(s, max);
    return s.trim();
}

/**
 * Pick the best definition from a localized definitions object,
 * with sensible fallbacks across CJK locales.
 */
export function pickLocalizedDefinition(
    definitions: Record<string, string> | undefined | null,
    locale: string,
    fallback?: string,
): string {
    const defs = definitions ?? {};
    const hasCJK = (s: string) => /[一-鿿㐀-䶿]/.test(s);

    let def = defs[locale];
    // If a Chinese locale fell back to English-only data, retry siblings.
    if (def && locale.startsWith('zh') && !hasCJK(def)) {
        def = defs['zh-CN'] || defs['zh-TW'] || def;
    }
    if (!def && locale === 'zh-TW') def = defs['zh-CN'];
    if (!def && locale === 'zh-CN') def = defs['zh-TW'];
    if (!def) def = defs.en;
    if (!def) def = fallback ?? '';
    return def;
}

/**
 * One-liner: pick localized + clean.
 */
export function getCleanLocalizedDefinition(
    definitions: Record<string, string> | undefined | null,
    locale: string,
    fallback?: string,
): string {
    return cleanDefinition(pickLocalizedDefinition(definitions, locale, fallback));
}
